<template>
	<StandardModal
		open
		class="forward-modal"
		:title="$t('chat_messageaction_forward_label')"
		:confirmText="
			$t(isLoading ? 'announcements_sending_hud' : 'chat_input_send_button')
		"
		:confirmProps="{ disabled, loading: isLoading }"
		:fullscreen="props.fullscreen"
		@confirm="sendAllMessages"
		@cancel="onClose"
		:tabOrder="[
			{ css: '.forward-recipients-container', clickOnEnter: true },
			{ all: '.media-preview-remove', clickOnEnter: true },
			{ css: '.ql-editor' },
		]"
	>
		<div class="forward-modal-content">
			<div class="forward-recipients-container" @click="showPicker = true">
				<div class="forward-recipients">
					<div
						class="recipient-to"
						v-text="$t('announcements_new_recipienttitle')"
					/>
					<div class="recipient-name" v-text="recipientName" />
					<div
						class="recipient-more"
						v-if="recipientChats.length > 1"
						v-text="$t('common_plus_more', [recipientChats.length - 1])"
					/>
				</div>
				<div class="forward-arrow">
					<div
						v-if="!recipientChats.length"
						v-text="$t('chat_messageaction_forward_select_label')"
					/>
					<ChevronIcon right />
				</div>
			</div>
			<MediaPreview
				v-if="mediaItems.length > 0"
				:mediaItems="mediaItems"
				:small="props.fullscreen"
				@remove="onRemoveMediaItem"
			/>
			<div
				class="forward-editor-container"
				:class="{ 'text-only': !mediaItems.length }"
			>
				<Editor
					v-model="text"
					:placeholder="$t('chat_input_text_placeholder')"
					disableEmojis
					disableMentions
					disableAutoResize
					enabled
					noDraft
					noClear
					allowNewLine
					autoFocus
				/>
			</div>
		</div>
	</StandardModal>

	<SelectionModal
		v-if="showPicker"
		:title="$t('chat_messageaction_forward_recipients_label')"
		:confirmText="$t('common_button_done')"
		:items="matchingChats"
		:columns="[{ field: 'displayname' }]"
		:selectedItemsById="recipientChatsById"
		:allowEmptySelection="recipientChats.length > 0"
		:fullscreen="props.fullscreen"
		:large="false"
		skipDiscard
		class="recipients-modal"
		@selection="onRecipientChange"
		@confirm="onSetRecipients"
		@cancel="showPicker = false"
	>
		<template #cell="{ data }">
			<div class="recipient-name">
				<div class="group" v-if="!data.isdm">
					<div
						v-if="data.category"
						class="category"
						v-text="data.category.displayname"
					/>
					<div class="group-name" v-text="data.displayname" />
				</div>
				<div class="dm" v-if="data.isdm">
					<ProfilePizza
						v-if="data.dmUserIds.length"
						:userIds="data.dmUserIds"
						size="40px"
						showTotal
					/>
					<div class="dm-name line-clamp-2" v-text="data.displayname" />
				</div>
			</div>
		</template>
		<template #filters>
			<div class="filters">
				<Input
					:placeholder="$t('chat_messageaction_forward_search_label')"
					:icon="SearchIcon"
					class="search"
					v-model="searchTerm"
				/>
			</div>
		</template>
	</SelectionModal>
</template>

<script setup>
import { ref, computed } from 'vue'
import {
	StandardModal,
	Input,
	ProfilePizza,
	useAlert,
} from '@/components/common'
import Editor, { replaceMentions } from '@/components/Editor'
import MediaPreview from '@/components/MessageInput/MediaPreview'
import SelectionModal from '@/components/OrgManagement/common/SelectionModal'
import {
	getMessage,
	getChats,
	getCategory,
	sortChatsByDisplayName,
	getMyUserId,
	setMessage,
	getUsersForBubble,
} from '@/store'
import { ChevronIcon, SearchIcon } from '@/icons'
import { CREATE_MESSAGE, chatCan } from '@/helpers/permissions'
import escapeRegEx from 'escape-string-regexp'
import moment from 'moment'
import { useI18n } from 'vue-i18n'
import { sendMessage } from '@/api/message'

const props = defineProps({
	messageId: [String, Number],
	fullscreen: { type: Boolean, default: false },
})
const emit = defineEmits(['close'])
const alert = useAlert()
const { t } = useI18n()

const message = getMessage(props.messageId)
const text = ref(message.message ? replaceMentions(message.message) : '')
const mediaItems = ref(message.messagemedia || [])
const searchTerm = ref('')
const showPicker = ref(false)
const isLoading = ref(false)
const recipientChatsById = ref({})
const recipientChats = ref([])

const allChats = ref(getChatsFromStore())

const matchingChats = computed(() => {
	const chats = allChats.value
	if (!searchTerm.value) return chats
	const regex = new RegExp(escapeRegEx(searchTerm.value), 'i')
	return chats.filter(
		(c) => regex.test(c.displayname) || regex.test(c.category?.displayname)
	)
})

const recipientName = computed(
	() => recipientChats.value?.[0]?.displayname || ''
)

const disabled = computed(() => {
	if (!recipientChats.value.length) return true
	if (!text.value?.trim() && !mediaItems.value.length) return true
	return false
})

function onClose() {
	emit('close')
}

function onRecipientChange(selected = {}) {
	recipientChatsById.value = selected
}

function onSetRecipients() {
	showPicker.value = false
	recipientChats.value = Object.values(recipientChatsById.value)
		.filter((x) => x.selected === true)
		.map((x) => x.item)
		.sort(sortChatsByDisplayName)
}

function onRemoveMediaItem(item) {
	mediaItems.value = mediaItems.value.filter((m) => m.id !== item.id)
}

function generateMessagePayload(data = {}) {
	const payload = {
		id: null,
		uuid: crypto.randomUUID(),
		created_at: moment().utc().format('YYYY-MM-DD HH:mm:ss'),
		sendState: 'waiting',
		user_id: getMyUserId(),
		replace_string_mentions: true,
		message: text.value.trim(),
		...data,
	}

	if (mediaItems.value.length) {
		payload.messagemedia = mediaItems.value
	}

	return payload
}

async function sendAllMessages() {
	if (disabled.value || isLoading.value) return
	try {
		isLoading.value = true
		const promises = recipientChats.value.map((chat) => {
			const message = generateMessagePayload({ bubble_id: chat.id })
			setMessage(message)
			return sendMessage(message)
		})
		await Promise.all(promises)
		onClose()
		alert.success(t('common_confirmation_sent_nocheck'))
	} catch (e) {
		// sending messages is "best effort" so we don't show any errors to
		// the end user, instead just leave those messages in an error state
		console.error(e)
	}
	isLoading.value = false
}

function getChatsFromStore() {
	return getChats()
		.filter((c) => {
			if (!chatCan(c, CREATE_MESSAGE)) return false
			if (c.isdm) {
				const allDeactivated = getUsersForBubble(c.id).every(
					(u) => u.id === getMyUserId() || u.deactivated_at
				)
				if (allDeactivated) return false
			}
			return true
		})
		.map((c) => ({
			...c,
			dmUserIds: c.isdm ? getUsersForBubble(c.id).map((u) => u.id) : [],
			category: getCategory(c.category_id),
		}))
		.sort(sortChatsByDisplayName)
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.forward-modal-content {
	@include flex-column;
	justify-content: stretch;
	height: 100%;

	.forward-recipients-container {
		@include flex-spread;
		height: 55px;
		padding: 0 16px;
		cursor: pointer;
		flex-shrink: 0;
		border-bottom: 1px solid var(--neutral-200);

		.forward-recipients {
			max-width: 100%;
			@include flex(8px);
			flex-wrap: nowrap;

			.recipient-to {
				@include fontFamily(800);
			}
			.recipient-name {
				@include ellipsis;
				font-size: 16px;
			}
			.recipient-more {
				@include fontFamily(900);
				color: var(--blue-300);
				white-space: nowrap;
				flex-shrink: 0;
			}
		}

		.forward-arrow {
			@include flex(8px);
			@include fontFamily(400);
			color: var(--neutral-600);
			font-size: 17px;
			&:hover {
				color: var(--neutral-800);
			}
		}
	}
	.media-preview-container {
		padding: 12px 0 16px 0;
		flex-shrink: 0;
		// force scrollbar to always be visible in macOS so that overflowing
		// media items are more clearly noticeable
		@include forceVisibleMacScrollbar(true);
		:deep(.media-preview-items) {
			padding: 12px 24px 0 24px;
		}
	}
	.forward-editor-container {
		flex-grow: 1;
		overflow: auto;
		&.text-only {
			:deep(.ql-editor) {
				padding-top: 16px !important;
			}
		}

		.pronto-editor {
			height: 100% !important;
			padding: 0;
			max-height: unset !important;
			--max-height: 100% !important;
			--padding: 0 !important;

			@media screen and (max-width: 800px) {
				width: 100% !important;
			}

			:deep(.ql-editor) {
				max-height: 100% !important;
				padding: 0 16px 16px 16px;
				&::before {
					left: 16px;
				}
			}
			:deep(.emoji_completions) {
				max-height: 100px;
				overflow-y: auto;
			}
		}
	}
}

.recipient-name {
	.group {
		font-family: Avenir;
		font-style: normal;
		color: var(--neutral-800);

		.category {
			font-weight: normal;
			font-size: 12px;
			line-height: 16px;
			text-transform: uppercase;
			@include ellipsis;
		}

		.group-name {
			@include fontFamily(800);
			font-size: 15px;
			line-height: 20px;
		}
	}
	.dm {
		@include flex(16px);
		.dm-name {
			@include fontFamily(400);
		}
	}
	.profile-pizza {
		flex-shrink: 0;
	}
}
</style>
